/* ============================================================
   FERAL Header — interactions
   - <feral-header>: sticky scroll state
   - Mega-menu hover/click/touch
   - Mobile drawer
   - Search overlay + predictive results
   - Live cart count updates
   ============================================================ */
(function () {
  'use strict';

  /* ─── Portal overlays to body ──────────────────────────────
     The header section wrapper (.feral-h-section) has z-index: 90,
     which creates a stacking context. Anything inside it — drawer,
     search overlay — has its own z-index trapped inside that context.
     The announcement bar (z-index 95 in body's stacking context)
     paints on top of them as a result. Portaling to body lets them
     compare z-indices directly and stack properly. */
  function portalOverlays() {
    ['feral-drawer', 'feral-search'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', portalOverlays);
  } else {
    portalOverlays();
  }

  /* ─── Sticky scroll state ──────────────────────────────── */
  class FeralHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sticky === 'false') return;
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.classList.toggle('is-scrolled', window.scrollY > 8);
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }
  if (!customElements.get('feral-header')) customElements.define('feral-header', FeralHeader);

  /* ─── Mega-menu (touch + click awareness) ─────────────── */
  document.querySelectorAll('[data-feral-mega]').forEach((item) => {
    const trigger = item.querySelector('.feral-h__nav-link');
    if (!trigger) return;

    // Click toggles open state (desktop)
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const isOpen = item.classList.contains('is-open');
      document.querySelectorAll('[data-feral-mega].is-open').forEach((other) => {
        if (other !== item) other.classList.remove('is-open');
        const otherBtn = other.querySelector('.feral-h__nav-link');
        if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
      });
      if (isOpen) {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });

    // Hover handles desktop too — clear is-open when leaving
    item.addEventListener('mouseleave', () => {
      // If clicked-open, hover-leave doesn't close (so user can move into menu)
      // We rely on click outside to close.
    });

    // Hover-enter on a DIFFERENT nav item closes any click-open sibling.
    // Without this, a click-opened panel stays mounted while you hover another
    // — both render at once because the CSS shows panels on :hover OR .is-open.
    item.addEventListener('mouseenter', () => {
      document.querySelectorAll('[data-feral-mega].is-open').forEach((other) => {
        if (other !== item) {
          other.classList.remove('is-open');
          const otherBtn = other.querySelector('.feral-h__nav-link');
          if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
        }
      });
    });
  });

  // Click outside to close mega menus
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-feral-mega]')) {
      document.querySelectorAll('[data-feral-mega].is-open').forEach((m) => {
        m.classList.remove('is-open');
        const btn = m.querySelector('.feral-h__nav-link');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }
  });

  // ESC closes mega menus
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('[data-feral-mega].is-open').forEach((m) => {
        m.classList.remove('is-open');
        const btn = m.querySelector('.feral-h__nav-link');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* ─── Mobile drawer ────────────────────────────────────── */
  const drawer = document.getElementById('feral-drawer');
  const drawerToggles = document.querySelectorAll('[data-feral-toggle="drawer"]');
  const drawerCloses = document.querySelectorAll('[data-feral-drawer-close]');

  function openDrawer() {
    if (!drawer) return;
    drawer.hidden = false;
    drawer.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Force reflow before adding the open class so transform transitions actually run
    void drawer.offsetWidth;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => drawer.classList.add('is-open'));
    });
    drawerToggles.forEach((b) => b.setAttribute('aria-expanded', 'true'));
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    drawerToggles.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => { drawer.hidden = true; drawer.setAttribute('hidden', ''); }, 420);
  }
  drawerToggles.forEach((b) => b.addEventListener('click', (e) => {
    e.preventDefault();
    if (drawer.hidden || !drawer.classList.contains('is-open')) openDrawer();
    else closeDrawer();
  }));
  drawerCloses.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); closeDrawer(); }));

  /* ─── Drawer tabs + sliding sub-panels ─────────────────── */
  const drawerTabs = drawer ? drawer.querySelectorAll('.feral-drawer__tab') : [];
  const drawerPanels = drawer ? drawer.querySelectorAll('.feral-drawer__panel-card') : [];

  function activatePanel(panelId) {
    drawerPanels.forEach((p) => {
      p.classList.toggle('is-active', p.dataset.panelId === panelId);
    });
  }

  function activateTab(tabId) {
    drawerTabs.forEach((t) => {
      const isActive = t.dataset.tab === tabId;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    // Reset to root panel of this tab whenever switching tabs
    activatePanel(tabId);
  }

  drawerTabs.forEach((t) => {
    t.addEventListener('click', () => activateTab(t.dataset.tab));
  });

  // Open a sub-panel (via [data-open-panel="..."])
  if (drawer) {
    drawer.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open-panel]');
      if (opener) {
        e.preventDefault();
        const targetId = opener.dataset.openPanel;
        activatePanel(targetId);
        // Scroll to top of sub-panel for smooth feel
        requestAnimationFrame(() => {
          const target = drawer.querySelector('[data-panel-id="' + targetId + '"]');
          if (target) target.scrollTop = 0;
        });
      }
      const closer = e.target.closest('[data-close-panel]');
      if (closer) {
        e.preventDefault();
        const parentId = closer.dataset.closePanel;
        activatePanel(parentId);
      }
    });
  }

  // Reset drawer to default panel whenever it opens
  if (drawer) {
    const observer = new MutationObserver(() => {
      if (drawer.classList.contains('is-open')) {
        const defaultPanel = drawer.querySelector('[data-default="true"]');
        if (defaultPanel) {
          const tab = defaultPanel.dataset.tab;
          if (tab) activateTab(tab);
          else activatePanel(defaultPanel.dataset.panelId);
        }
      }
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ─── Search overlay ───────────────────────────────────── */
  const search = document.getElementById('feral-search');
  const searchToggles = document.querySelectorAll('[data-feral-toggle="search"]');
  const searchCloses = document.querySelectorAll('[data-feral-search-close]');

  function openSearch() {
    if (!search) return;
    search.hidden = false;
    search.removeAttribute('hidden');
    search.setAttribute('aria-hidden', 'false');
    void search.offsetWidth;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => search.classList.add('is-open'));
    });
    setTimeout(() => {
      const input = search.querySelector('input[type="search"]');
      if (input) input.focus();
    }, 200);
  }
  function closeSearch() {
    if (!search) return;
    search.classList.remove('is-open');
    search.setAttribute('aria-hidden', 'true');
    setTimeout(() => { search.hidden = true; search.setAttribute('hidden', ''); }, 420);
  }
  searchToggles.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); openSearch(); }));
  searchToggles.forEach((b) => {
    // Inline searchbar form: any focus on the inline readonly input also opens the overlay
    const innerInput = b.querySelector('input[type="search"]');
    if (innerInput) {
      innerInput.addEventListener('focus', (e) => { e.preventDefault(); openSearch(); innerInput.blur(); });
    }
    // Submitting form (enter) routes to /search; intercept to open overlay instead
    if (b.tagName === 'FORM') {
      b.addEventListener('submit', (e) => { e.preventDefault(); openSearch(); });
    }
  });
  searchCloses.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); closeSearch(); }));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
      closeSearch();
    }
  });

  /* ─── Recently-viewed tracking (run on every page load) ──── */
  (function () {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) return;
    const handle = m[1];
    if (!handle || handle === 'undefined') return;
    const KEY = 'feral_recent_v2';
    const MAX = 12;
    let recent = [];
    try { recent = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (_) {}
    if (!Array.isArray(recent)) recent = [];
    recent = [handle, ...recent.filter((h) => h && h !== handle)].slice(0, MAX);
    try { localStorage.setItem(KEY, JSON.stringify(recent)); } catch (_) {}
  })();

  /* ─── Search overlay rest state — recently viewed grid ──── */
  const recentGrid = document.querySelector('[data-feral-recent-grid]');
  const recentLabel = document.querySelector('[data-feral-recent-label]');
  const recentPrev = document.querySelector('[data-feral-recent-prev]');
  const recentNext = document.querySelector('[data-feral-recent-next]');
  const fallbackHandle = (search && search.dataset.fallbackCollection) || 'shop-all';
  let recentLoaded = false;

  function getProductImage(p) {
    if (p.featured_image) {
      if (typeof p.featured_image === 'string') return p.featured_image;
      if (p.featured_image.url) return p.featured_image.url;
      if (p.featured_image.src) return p.featured_image.src;
    }
    if (p.image) {
      if (typeof p.image === 'string') return p.image;
      if (p.image.src) return p.image.src;
      if (p.image.url) return p.image.url;
    }
    if (p.images && p.images.length > 0) {
      const first = p.images[0];
      if (typeof first === 'string') return first;
      if (first.src) return first.src;
      if (first.url) return first.url;
    }
    return '';
  }

  function normalizeUrl(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  function priceToStr(cents) {
    if (!cents || cents === 0) return '';
    return '£' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function renderRecentCards(products) {
    if (!recentGrid) return;
    if (!products || products.length === 0) {
      recentGrid.innerHTML = '';
      const section = recentGrid.closest('.feral-search__recent');
      if (section) section.style.display = 'none';
      return;
    }
    const html = products.slice(0, 8).map((p) => {
      const imgUrl = normalizeUrl(getProductImage(p));
      const priceCents = typeof p.price === 'number' ? p.price : 0;
      const compareCents = typeof p.compare_at_price === 'number' ? p.compare_at_price : 0;
      const priceStr = priceToStr(priceCents);
      const compareStr = compareCents > priceCents ? priceToStr(compareCents) : '';
      const pct = compareCents > priceCents ? Math.round((1 - priceCents / compareCents) * 100) : 0;
      const tagsArr = Array.isArray(p.tags) ? p.tags : (typeof p.tags === 'string' ? p.tags.split(',').map((t) => t.trim()) : []);
      const isNew = tagsArr.some((t) => /^new$/i.test(t));
      let badge = '';
      if (pct > 0) badge = '<span class="feral-search__pcard-badge">' + pct + '% OFF</span>';
      else if (isNew) badge = '<span class="feral-search__pcard-badge feral-search__pcard-badge--new">NEW</span>';
      return ''
        + '<a href="' + (p.url || '/products/' + (p.handle || '')) + '" class="feral-search__pcard">'
        +   '<div class="feral-search__pcard-media">'
        +     badge
        +     '<button type="button" class="feral-search__pcard-heart" aria-label="Save" tabindex="-1"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg></button>'
        +     (imgUrl ? '<img src="' + imgUrl + '" alt="" loading="lazy">' : '')
        +   '</div>'
        +   '<div class="feral-search__pcard-info">'
        +     '<div class="feral-search__pcard-title">' + (p.title || '') + '</div>'
        +     (priceStr ? '<div class="feral-search__pcard-price">' + (compareStr ? '<s>' + compareStr + '</s>' : '') + priceStr + '</div>' : '')
        +   '</div>'
        + '</a>';
    }).join('');
    recentGrid.innerHTML = html;
  }

  function loadRecentlyViewed() {
    if (recentLoaded || !recentGrid) return;
    recentLoaded = true;
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem('feral_recent_v2') || '[]'); } catch (_) {}
    handles = (handles || []).filter(Boolean);

    if (handles.length === 0) {
      // Fall back to best sellers from the configured collection
      if (recentLabel) recentLabel.textContent = 'Best sellers';
      fetch('/collections/' + fallbackHandle + '/products.json?limit=8')
        .then((r) => r.ok ? r.json() : { products: [] })
        .then((data) => renderRecentCards(data.products || []))
        .catch(() => renderRecentCards([]));
    } else {
      Promise.all(
        handles.slice(0, 8).map((h) =>
          fetch('/products/' + h + '.js')
            .then((r) => r.ok ? r.json() : null)
            .catch(() => null)
        )
      ).then((products) => renderRecentCards(products.filter(Boolean)));
    }
  }

  // Carousel arrows
  if (recentPrev && recentGrid) recentPrev.addEventListener('click', () => recentGrid.scrollBy({ left: -360, behavior: 'smooth' }));
  if (recentNext && recentGrid) recentNext.addEventListener('click', () => recentGrid.scrollBy({ left: 360, behavior: 'smooth' }));

  /* ─── Predictive search ────────────────────────────────── */
  const searchInput = document.querySelector('[data-feral-search-input]');
  const searchResults = document.querySelector('[data-feral-search-results]');
  const searchRest = document.querySelector('[data-feral-search-rest]');
  if (searchInput && searchResults) {
    let debounceT;

    function moneyFromAmount(amount) {
      if (typeof amount === 'number') return '£' + (amount).toFixed(2).replace(/\.00$/, '');
      if (typeof amount === 'string') return amount;
      if (amount && typeof amount.amount === 'number') return '£' + amount.amount.toFixed(2).replace(/\.00$/, '');
      return '';
    }

    function renderResults(data) {
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      const articles = (data.resources && data.resources.results && data.resources.results.articles) || [];
      const queries = (data.resources && data.resources.results && data.resources.results.queries) || [];

      if (!products.length && !articles.length && !queries.length) {
        searchResults.innerHTML = '<div class="feral-search__empty">No matches. Try another keyword.</div>';
        return;
      }

      let html = '';
      if (queries.length) {
        html += '<div class="feral-search__group"><div class="feral-search__group-label">Suggestions</div>';
        queries.slice(0, 6).forEach((q) => {
          html += '<a href="/search?q=' + encodeURIComponent(q.text) + '" class="feral-search__suggestion">' + q.text + '</a>';
        });
        html += '</div>';
      }
      if (products.length) {
        html += '<div class="feral-search__group"><div class="feral-search__group-label">Products</div><div class="feral-search__products">';
        products.slice(0, 6).forEach((p) => {
          const img = p.featured_image ? p.featured_image.url : '';
          html += '<a href="' + p.url + '" class="feral-search__product">'
            + '<div class="feral-search__product-img">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</div>'
            + '<div><div class="feral-search__product-name">' + p.title + '</div>'
            + (p.price ? '<div class="feral-search__product-price">' + moneyFromAmount(p.price) + '</div>' : '')
            + '</div></a>';
        });
        html += '</div></div>';
      }
      if (articles.length) {
        html += '<div class="feral-search__group"><div class="feral-search__group-label">Journal</div>';
        articles.slice(0, 4).forEach((a) => {
          html += '<a href="' + a.url + '" class="feral-search__article">' + a.title + '</a>';
        });
        html += '</div>';
      }
      searchResults.innerHTML = html;
    }

    searchInput.addEventListener('input', function () {
      const q = this.value.trim();
      clearTimeout(debounceT);
      if (q.length < 2) {
        // Show rest state, hide predictive results
        if (searchRest) searchRest.hidden = false;
        searchResults.hidden = true;
        searchResults.innerHTML = '';
        return;
      }
      // Hide rest state, show predictive results
      if (searchRest) searchRest.hidden = true;
      searchResults.hidden = false;
      debounceT = setTimeout(() => {
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product,article,query&resources[limit]=8')
          .then((r) => r.json())
          .then(renderResults)
          .catch(() => {});
      }, 160);
    });
  }

  // When the overlay opens, load recently viewed (first time only)
  if (search) {
    const observer = new MutationObserver(() => {
      if (search.classList.contains('is-open')) loadRecentlyViewed();
    });
    observer.observe(search, { attributes: true, attributeFilter: ['class'] });
  }

  /* ─── Live cart count ──────────────────────────────────── */
  // Patches fetch to detect /cart/add and /cart/change responses
  const originalFetch = window.fetch;
  window.fetch = function (url, opts) {
    const result = originalFetch.apply(this, arguments);
    const u = typeof url === 'string' ? url : (url && url.url) || '';
    if (u.includes('/cart/add') || u.includes('/cart/change') || u.includes('/cart/update') || u.includes('/cart/clear')) {
      result.then((res) => {
        if (!res || !res.ok) return;
        fetch('/cart.js').then((r) => r.json()).then((cart) => {
          document.querySelectorAll('[data-feral-cart-count]').forEach((el) => {
            el.textContent = String(cart.item_count);
            el.classList.toggle('is-hidden', cart.item_count === 0);
          });
        }).catch(() => {});
      }).catch(() => {});
    }
    return result;
  };
})();
